import { Model } from 'pinia-orm';
import { validUrl } from '@/shared/utils/validUrl';

export class Page extends Model {
  static entity = 'pages';
  static primaryKey = 'id';

  static fields() {
    return {
      id: this.uid(),
      experimentId: this.string(''),
      name: this.string(''),
      defaultUrl: this.string(''),
      createdAt: this.attr(null),
      updatedAt: this.attr(null),
    };
  }

  declare id: string | null;
  declare experimentId: string;
  declare name: string;
  declare defaultUrl: string;
  declare createdAt: string | null;
  declare updatedAt: string | null;

  private _originalValues: Record<string, any> = {};

  static hydrate(data: any) {
    const page = new this(data);
    page._setOriginalValues();
    return page;
  }

  _setOriginalValues() {
    this._originalValues = {
      name: this.name,
      defaultUrl: this.defaultUrl,
    };
  }

  get hasChanges(): boolean {
    return Object.keys(this._originalValues).some(
      (key) => this._originalValues[key] !== (this as any)[key]
    );
  }

  get isValid(): boolean {
    return this.name.trim().length > 0 && this.isValidDefaultUrl();
  }

  get isValidDefaultUrl(): boolean {
    return this.defaultUrl.trim().length === 0 || validUrl(this.defaultUrl);
  }

  static fromAPI(apiData: any): Page {
    return this.hydrate({
      id: apiData.id,
      experimentId: apiData.experiment_id || apiData.experimentId,
      name: apiData.name || '',
      defaultUrl: apiData.default_url || apiData.defaultUrl || '',
      createdAt: apiData.created_at || apiData.createdAt,
      updatedAt: apiData.updated_at || apiData.updatedAt,
    });
  }

  toAPI(): any {
    return {
      id: this.id,
      experiment_id: this.experimentId,
      name: this.name,
      default_url: this.defaultUrl,
    };
  }

  reset() {
    Object.keys(this._originalValues).forEach((key) => {
      (this as any)[key] = this._originalValues[key];
    });
  }

  updateOriginalValues() {
    this._setOriginalValues();
  }
}

export class UrlRule extends Model {
  static entity = 'urlRules';
  static primaryKey = 'id';

  static fields() {
    return {
      id: this.uid(),
      pageId: this.string(''),
      experimentId: this.string(''),
      includes: this.boolean(true),
      type: this.string('url_equals'),
      value: this.string(''),
      createdAt: this.attr(null),
      updatedAt: this.attr(null),
    };
  }

  declare id: string | null;
  declare pageId: string;
  declare experimentId: string;
  declare includes: boolean;
  declare type: string;
  declare value: string;
  declare createdAt: string | null;
  declare updatedAt: string | null;

  private _originalValues: Record<string, any> = {};

  static hydrate(data: any) {
    const urlRule = new this(data);
    urlRule._setOriginalValues();
    return urlRule;
  }

  _setOriginalValues() {
    this._originalValues = {
      includes: this.includes,
      type: this.type,
      value: this.value,
    };
  }

  get hasChanges(): boolean {
    return Object.keys(this._originalValues).some(
      (key) => this._originalValues[key] !== (this as any)[key]
    );
  }

  get isValid(): boolean {
    return this.value.trim().length > 0 && this.isValidUrlPattern();
  }

  private isValidUrlPattern(): boolean {
    switch (this.type) {
      case 'url_equals':
        return validUrl(this.value);
      case 'url_matches_pattern':
        return this.value.includes('*');
      case 'url_matches_regex':
        try {
          new RegExp(this.value);
          return true;
        } catch (e) {
          return false;
        }
      default:
        return this.value.trim().length > 0;
    }
  }

  get displayLabel(): string {
    return this.includes ? 'Include' : 'Exclude';
  }

  get displayColor(): string {
    return 'text-gray-700';
  }

  get displayDot(): string {
    return this.includes ? 'bg-green-500' : 'bg-red-500';
  }

  static fromAPI(apiData: any): UrlRule {
    return this.hydrate({
      id: apiData.id,
      pageId: apiData.page_id || apiData.pageId,
      experimentId: apiData.experiment_id || apiData.experimentId,
      includes: apiData.includes ?? true,
      type: apiData.type || 'url_equals',
      value: apiData.value || '',
      createdAt: apiData.created_at || apiData.createdAt,
      updatedAt: apiData.updated_at || apiData.updatedAt,
    });
  }

  toAPI(): any {
    return {
      id: this.id,
      page_id: this.pageId,
      experiment_id: this.experimentId,
      includes: this.includes,
      type: this.type,
      value: this.value,
    };
  }

  reset() {
    Object.keys(this._originalValues).forEach((key) => {
      (this as any)[key] = this._originalValues[key];
    });
  }

  updateOriginalValues() {
    this._setOriginalValues();
  }

  // URL matching type options
  static getTypeOptions() {
    return [
      { value: 'url_equals', label: 'URL equals exactly' },
      { value: 'url_contains', label: 'URL contains text' },
      { value: 'url_starts_with', label: 'URL starts with text' },
      { value: 'url_ends_with', label: 'URL ends with text' },
      { value: 'url_matches_pattern', label: 'URL matches pattern' },
      { value: 'url_matches_regex', label: 'URL matches regex' },
    ];
  }

  // Create initial URL rule data
  static createInitialData(pageId: string, experimentId: string): Partial<UrlRule> {
    return {
      pageId,
      experimentId,
      includes: true,
      type: 'url_equals',
      value: 'https://',
    };
  }
}
